import React from "react";
import moment from "moment-timezone";

import connectToStores from "alt-utils/lib/connectToStores";
import BoxOfficeActions from "../actions/BoxOfficeActions";
import BoxOfficeStore from "../stores/BoxOfficeStore";
import EventBucketStore from "../stores/EventBucketStore";
import EventStore from "../stores/EventStore";
import PassBucketStore from "../stores/PassBucketStore";
import ItemBucketStore from "../stores/ItemBucketStore";
import UserStore from "../stores/UserStore";
import ShiftStore from "../stores/ShiftStore";
import PassBucketActions from "../actions/PassBucketActions";
import ItemBucketActions from "../actions/ItemBucketActions";
import EventActions from "../actions/EventActions";

import * as util from "../util";
import config from "../config";

import WorkingIndicator from "./partials/working-indicator.jsx";
import LinkLike from "./partials/link-like.jsx";
import SeatPickerOverlay from "./partials/overlays/seat-picker.jsx";

import BoxOfficeLastOrder from "./partials/box-office/box-office-last-order.jsx";
import BoxOfficeSubitemTable from "./partials/box-office/box-office-subitem-table.jsx";
import BoxOfficePassSetup from "./partials/box-office/box-office-pass-setup.jsx";
import BoxOfficePayment from "./partials/box-office/box-office-payment.jsx";
import BoxOfficeConfirm from "./partials/box-office/box-office-confirm.jsx";
import BoxOfficeTerminal from "./partials/box-office/box-office-terminal.jsx";
import BoxOfficeShiftTracking from "./partials/box-office/box-office-shift-tracking.jsx";
import BoxOfficeDisplayModeControl from "./partials/box-office/box-office-display-mode-control.jsx";
import BoxOfficeGiftDetailsSetup from "./partials/box-office/box-office-gift-details-setup.jsx";

const BOX_OFFICE_TICKETS_PDF_WINDOW = "EventiveBoxOfficeTicketsPDF";
const BOX_OFFICE_PASSES_PDF_WINDOW = "EventiveBoxOfficePassesPDF";

class BoxOffice extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      refetchIntervalId: null,
      processedData: {
        subitems: [],
        total: 0,
        originalTotal: 0
      },
      lastOpenedOrder: null,
      openedSeatPickerEvent: null,
      hasReset: false
    };

    this._makeSubitemData = this._makeSubitemData.bind(this);
    this.openSeatPicker = this.openSeatPicker.bind(this);
    this.closeSeatPicker = this.closeSeatPicker.bind(this);
    this.onSeatPickerSubmit = this.onSeatPickerSubmit.bind(this);
  }

  static getStores() {
    return [
      BoxOfficeStore,
      EventBucketStore,
      EventStore,
      PassBucketStore,
      ItemBucketStore
    ];
  }

  static getPropsFromStores() {
    let state = BoxOfficeStore.getState();
    state.getPassBucketById = PassBucketStore.getPassBucketById;
    state.getTicketBucketWithEventById =
      EventStore.getTicketBucketWithEventById;
    state.getItemBucketById = ItemBucketStore.getItemBucketById;
    return state;
  }

  componentWillMount() {
    setTimeout(() => {
      BoxOfficeActions.reset({
        event_bucket: this.props.params.eventBucketId,
        customerType: "standard"
      });
      this.setState({ hasReset: true });
    }, 0);
  }

  componentDidMount() {
    // refetch pass buckets every 15 seconds to keep availability up-to-date
    const intervalId = setInterval(() => {
      PassBucketActions.fetchAllForEventBucket.defer(
        this.props.params.eventBucketId
      );
      ItemBucketActions.fetchAllForEventBucket.defer(
        this.props.params.eventBucketId
      );
      let eventBucket = EventBucketStore.getEventBucketById(
        this.props.params.eventBucketId
      );
    }, 15000);

    this.setState(prevState => ({
      ...prevState,
      refetchIntervalId: intervalId
    }));
  }

  componentWillUnmount() {
    clearInterval(this.state.refetchIntervalId);
  }

  componentWillReceiveProps(newProps) {
    let eventBucket = EventBucketStore.getEventBucketById(
      newProps.params.eventBucketId
    );
    if (this.props.params.eventBucketId !== newProps.params.eventBucketId) {
      let excludeOlderEvents = false;
      if (eventBucket.frontend_config.box_office_performance) {
        excludeOlderEvents = eventBucket.frontend_config.box_office_performance
          .exclude_older_events
          ? true
          : false;
      }
      EventActions.fetchAllForBucket.defer(
        newProps.params.eventBucketId,
        false,
        excludeOlderEvents
      );
    }

    let subitemData = [];
    (newProps.orderData.subitems || []).forEach((s, i) => {
      subitemData.push(
        this._makeSubitemData(s, i, {
          orderData: newProps.orderData,
          eventBucket,
          subitemData
        })
      );
    });

    let originalTotal = subitemData.reduce(
        (t, i) => t + (i.originalTotal || i.total),
        0
      ),
      total = subitemData.reduce((t, i) => t + i.total, 0);

    if (
      newProps.orderData.coupon &&
      newProps.orderData.coupon.gift_card &&
      newProps.orderData.coupon.gift_card_balance
    ) {
      originalTotal = originalTotal || total;
      total = Math.max(total - newProps.orderData.coupon.gift_card_balance, 0);
    }

    let processedData = {
      subitems: subitemData,
      mailingAddressRequired:
        subitemData.some(
          d => d.item && d.item.event && d.item.event.require_mailing_address
        ) ||
        (subitemData.some(d => d.item && d.item.code === "donation") &&
          EventBucketStore.getEventBucketById(newProps.params.eventBucketId)
            .frontend_config.donate_require_mailing_address),
      phoneNumberRequired:
        subitemData.some(
          d => d.item && d.item.event && d.item.event.require_phone_number
        ) ||
        (subitemData.some(d => d.item && d.item.code === "donation") &&
          EventBucketStore.getEventBucketById(newProps.params.eventBucketId)
            .frontend_config.donate_require_phone_number),
      originalTotal,
      total
    };

    if (newProps.orderData.group_sale_data) {
      let groupSize = newProps.orderData.group_sale_data.length;
      if (groupSize) {
        processedData.originalTotal *= groupSize;
        processedData.total *= groupSize;
        processedData.groupSalesSize = groupSize;
      }
    }

    this.setState({
      processedData
    });

    // Open window for success
    if (
      this.props.orderInProgress &&
      newProps.lastOrder &&
      this.state.lastOpenedOrder !== newProps.lastOrder.id &&
      EventBucketStore.getEventBucketById(newProps.params.eventBucketId)
        .box_office_auto_open
    ) {
      let win;

      this.setState({ lastOpenedOrder: newProps.lastOrder.id });

      if (newProps.lastOrder.tickets.some(t => !t.event.is_virtual)) {
        win = window.open(
          config.endpoint +
            newProps.lastOrder.order_tickets_pdf_path +
            "?via_box_office=true",
          BOX_OFFICE_TICKETS_PDF_WINDOW + Date.now()
        );
      } else if (newProps.lastOrder.passes.length) {
        win = window.open(
          config.endpoint +
            `passes/labels.pdf?${UserStore.makeApiKeyQueryString()}&conditions=${JSON.stringify(
              { _id: { $in: newProps.lastOrder.passes.map(p => p.id) } }
            )}`,
          BOX_OFFICE_PASSES_PDF_WINDOW + Date.now()
        );
      }

      if (win && win.focus) {
        win.focus();
      }
    }
  }

  _makeSubitemData(subitem, i, { orderData, eventBucket, subitemData }) {
    let item,
      variant,
      name,
      tooltip,
      descriptor,
      subname,
      price,
      originalPrice,
      variablePrice,
      quantity,
      tax,
      fee,
      total,
      originalTotal,
      seatQuantityEditor;
    let { getTicketBucketWithEventById, getPassBucketById, getItemBucketById } =
      this.props;

    if (subitem.ticket_bucket) {
      item = getTicketBucketWithEventById(subitem.ticket_bucket);
      name = `${item.event.name}`;
      descriptor = item.event.start_time
        ? `${moment
            .tz(item.event.start_time, item.event.timezone)
            .format("ddd MMM Do h:mm A")}`
        : "Undated event";
      if (subitem.variant) {
        variant = item.variants.find(v => v.id === subitem.variant);
        subname = `${item.name} – ${variant.name}`;
        price = variant.price;
      } else {
        subname = item.name;
      }
      if (subitem.seats && subitem.seats.length) {
        subname += ` (${subitem.seats.join(", ")})`;
      }
    } else if (subitem.pass_bucket) {
      item = getPassBucketById(subitem.pass_bucket);
      name = `${item.name}`;
    } else if (subitem.item_bucket) {
      item = getItemBucketById(subitem.item_bucket);
      variant = item.variants.find(v => v.id === subitem.variant);
      name = variant.name ? `${item.name} – ${variant.name}` : `${item.name}`;
      price = variant.price;
      variablePrice = variant.variable_price;
    } else if (subitem.gift_card) {
      item = subitem;
      name = `${item.name}`;
      price = item.price;
      variablePrice = item.variable_price;
    } else if (subitem.service_fee && subitem.auto) {
      const threshold = config.fee.service_fee_threshold.default;
      item = { service_fee: true };
      name = "Box office service fee";
      tooltip = `Charged on each item over ${util.formatPrice(threshold)}`;
      subitem.price = eventBucket.box_office_service_fee;
      subitem.quantity = subitemData.reduce((q, i, index) => {
        if (
          i.service_fee ||
          i.item.code === "service_fee" ||
          i.item.code === "donation"
        ) {
          return q;
        }

        const numEligible = Array.isArray(i.price)
          ? i.price.filter(p => p[0] >= threshold).reduce((q, d) => q + d[1], 0)
          : i.price >= threshold
            ? orderData.subitems[index].quantity
            : 0;

        return q + numEligible;
      }, 0);
    }

    price =
      typeof subitem.price !== "undefined"
        ? subitem.price
        : typeof price !== "undefined"
          ? price
          : typeof item.sale_price !== "undefined" && item.sale_price > 0 // nonzero only since 0 = unset
            ? item.sale_price
            : typeof item.price !== "undefined"
              ? item.price
              : 0;
    quantity = subitem.quantity;

    if (
      item.event &&
      item.event.venue &&
      item.event.venue.use_reserved_seating
    ) {
      if (orderData.customerType === "groupSales") {
        quantity = 1;
        seatQuantityEditor = (
          <div>
            1
            <div style={{ fontSize: "0.8em", lineHeight: 1 }}>
              Choose seat in group sale
            </div>
          </div>
        );
      } else {
        subitem.seats = subitem.seats || [];
        quantity = subitem.seats.length;
        seatQuantityEditor = (
          <div>
            {subitem.seats.length}
            <div style={{ fontSize: "0.8em", lineHeight: 1 }}>
              <LinkLike onTouchTap={() => this.openSeatPicker(item.event)}>
                Choose seats
              </LinkLike>
            </div>
          </div>
        );
      }
    }

    // Apply pass
    if (
      orderData.pass_data &&
      orderData.pass_data.subitems[i] &&
      orderData.pass_data.subitems[i].price !== price
    ) {
      originalPrice = price;

      if (
        orderData.pass_data.subitems[i].quantity_available &&
        subitem.quantity > orderData.pass_data.subitems[i].quantity_available
      ) {
        price = [
          [
            orderData.pass_data.subitems[i].price,
            orderData.pass_data.subitems[i].quantity_available
          ],
          [
            price,
            subitem.quantity -
              orderData.pass_data.subitems[i].quantity_available
          ]
        ];
      } else {
        price = orderData.pass_data.subitems[i].price;
      }

      originalTotal = originalPrice * quantity;
    }

    if (
      (orderData.coupon &&
        orderData.coupon.subitems &&
        orderData.coupon.subitems[i] &&
        orderData.coupon.subitems[i].is_applicable) ||
      (item && item.volume_price_enabled && !Array.isArray(price)) ||
      (item && item.sale_price) ||
      (eventBucket && eventBucket.tax_at_box_office) ||
      (eventBucket && eventBucket.fee_at_box_office)
    ) {
      if (Array.isArray(price)) {
        ({ tax, fee, total } = price.reduce(
          (d, p) => {
            const { tax: tax_, total: total_ } = util.applyCoupon(
              { ...subitem, quantity: p[1] },
              p[0],
              item,
              orderData.coupon && orderData.coupon.subitems[i].price,
              eventBucket
            );
            return { tax: d.tax + tax_, total: d.total + total_ };
          },
          { tax: 0, fee: 0, total: 0 }
        ));
      } else {
        ({ originalPrice, price, tax, fee, total } = util.applyCoupon(
          subitem,
          price,
          item,
          orderData.coupon &&
            orderData.coupon.subitems &&
            orderData.coupon.subitems[i] &&
            orderData.coupon.subitems[i].price,
          eventBucket
        ));
        originalTotal = originalPrice * quantity;
      }
    } else {
      total = Array.isArray(price)
        ? price.reduce((total, p) => total + p[0] * p[1], 0)
        : price * subitem.quantity;
    }

    return {
      item,
      name,
      descriptor,
      tooltip,
      subname,
      price,
      originalPrice,
      quantity: seatQuantityEditor || quantity,
      fixedQuantity: !!seatQuantityEditor || subitem.service_fee,
      noDelete: subitem.service_fee && subitem.auto,
      hidden: subitem.service_fee && total === 0,
      variablePrice: variablePrice,
      tax,
      fee,
      total,
      originalTotal
    };
  }

  openSeatPicker(event) {
    this.setState({
      openedSeatPickerEvent: event
    });
  }

  closeSeatPicker() {
    this.setState({ openedSeatPickerEvent: null });
  }

  onSeatPickerSubmit(pickedSubitems) {
    let updatedSubitems = this.props.orderData.subitems.slice();

    // Remove all previous subitems for this event since we'll be resetting them all
    // with the latest new data from the seat picker.
    const affectedTicketBuckets =
      this.state.openedSeatPickerEvent.ticket_buckets.map(b => b.id);
    updatedSubitems = updatedSubitems.filter(
      s => affectedTicketBuckets.indexOf(s.ticket_bucket) === -1
    );

    updatedSubitems = updatedSubitems.concat(pickedSubitems);

    BoxOfficeActions.updateCurrentOrder({
      subitems: updatedSubitems
    });
    this.setState({
      openedSeatPickerEvent: null
    });
  }

  getStyles() {
    return {
      header: {
        textAlign: "left",
        marginBottom: "0.5em",
        marginTop: "0.3em"
      },
      headerWithShiftTracking: {},
      subheaderStyle: {
        marginTop: "2em",
        color: "#0288d1",
        borderBottom: "1px solid",
        paddingBottom: 5,
        width: 100
      },
      topOptions: {
        float: "right",
        marginTop: -5
      }
    };
  }

  render() {
    const styles = this.getStyles();

    let {
        orderData,
        orderInProgress,
        orderErrorMessage,
        lastOrder,
        location,
        terminal,
        terminalConnectionStatus,
        terminalPaymentStatus,
        externalNeedsConfirm,
        displayMode,
        posConfig
      } = this.props,
      { processedData, hasReset } = this.state,
      bucket = EventBucketStore.getEventBucketById(
        this.props.params.eventBucketId
      );

    if (!bucket || !orderData || !orderData.subitems || !hasReset) {
      return <WorkingIndicator fullPage={true} />;
    }

    return (
      <div style={displayMode === "pos" ? { minWidth: 900 } : null}>
        {this.state.openedSeatPickerEvent ? (
          <SeatPickerOverlay
            order={orderData}
            event={this.state.openedSeatPickerEvent}
            onRequestClose={this.closeSeatPicker}
            onSubmit={this.onSeatPickerSubmit}
          />
        ) : null}
        <div style={styles.topOptions}>
          <BoxOfficeTerminal
            initializeTerminal={BoxOfficeActions.initializeTerminal}
            terminal={terminal}
            terminalConnectionStatus={terminalConnectionStatus}
            terminalPaymentStatus={terminalPaymentStatus}
            processedData={processedData}
            orderInProgress={orderInProgress}
          />
          <BoxOfficeDisplayModeControl />
          {bucket.box_office_shift_tracking ? <BoxOfficeShiftTracking /> : null}
        </div>
        <h1
          style={Object.assign(
            {},
            styles.header,
            bucket.box_office_shift_tracking && styles.headerWithShiftTracking
          )}
        >
          {bucket.name} Box Office
        </h1>
        <BoxOfficeLastOrder
          lastOrder={lastOrder}
          repeatLastOrder={() => {
            let newOrder = { ...lastOrder._raw_order };
            newOrder.subitems = newOrder.subitems.map(s => {
              const { seats, ...rest } = s;
              return rest;
            });
            BoxOfficeActions.updateCurrentOrder(newOrder);
          }}
        />
        <BoxOfficeSubitemTable
          displayMode={displayMode}
          posConfig={(posConfig || {})[bucket.id] || {}}
          orderData={orderData}
          processedData={processedData}
          updateCurrentOrder={BoxOfficeActions.updateCurrentOrder}
          updatePosConfig={config =>
            BoxOfficeActions.updatePosConfig({ [bucket.id]: config })
          }
        />
        <BoxOfficeGiftDetailsSetup
          orderData={orderData}
          updateCurrentOrder={BoxOfficeActions.updateCurrentOrder}
          headerStyle={styles.subheaderStyle}
        />
        <BoxOfficePassSetup
          orderData={orderData}
          updateCurrentOrder={BoxOfficeActions.updateCurrentOrder}
          headerStyle={styles.subheaderStyle}
        />
        <BoxOfficePayment
          orderData={orderData}
          updateCurrentOrder={BoxOfficeActions.updateCurrentOrder}
          processedData={processedData}
          location={location}
          eventBucket={bucket}
          headerStyle={styles.subheaderStyle}
          terminalConnectionStatus={terminalConnectionStatus}
        />
        <BoxOfficeConfirm
          orderData={orderData}
          updateCurrentOrder={BoxOfficeActions.updateCurrentOrder}
          submitOrder={BoxOfficeActions.submitOrder}
          processedData={processedData}
          orderInProgress={orderInProgress}
          orderErrorMessage={orderErrorMessage}
          eventBucket={bucket}
          headerStyle={styles.subheaderStyle}
          externalNeedsConfirm={externalNeedsConfirm}
          cancelExternal={BoxOfficeActions.cancelExternal}
          showEmailSuppressionCheckbox={
            UserStore.isGod() ||
            bucket.frontend_config.sundance_login ||
            bucket.frontend_config.box_office_email_suppression_option_enabled
          }
        />
      </div>
    );
  }
}

export default connectToStores(BoxOffice);



// WEBPACK FOOTER //
// ./src/app/components/box-office.jsx